import React, { useState, useRef, useEffect } from "react";
import { IconZoomIn, IconZoomOut, IconRotateDot, IconHandGrab } from "@tabler/icons-react";

/**
 * Görüntüleri fare tekerleğiyle yakınlaştırma ve sürükleyerek (drag) gezme 
 * özelliğine sahip, kontrol butonları barındıran etkileşimli resim bileşeni.
 */
export const ZoomPanImage = ({ src, alt, className = "", style = {} }) => {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // Resim kaynağı değiştiğinde yakınlaştırmayı sıfırla
    useEffect(() => {
        handleReset();
    }, [src]);

    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.5, 6));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev - 0.5, 1));
    };

    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const zoomIntensity = 0.12;
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const delta = -e.deltaY;
        const factor = delta > 0 ? (1 + zoomIntensity) : (1 - zoomIntensity);
        const newScale = Math.max(1, Math.min(6, scale * factor));

        if (newScale !== scale) {
            // İmlecin konumuna göre odaklayarak yakınlaştır/uzaklaştır
            const xs = (mouseX - position.x) / scale;
            const ys = (mouseY - position.y) / scale;
            
            setScale(newScale);
            setPosition({
                x: mouseX - xs * newScale,
                y: mouseY - ys * newScale
            });
        }
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Birebir ölçekte görüntüyü merkeze oturt
    useEffect(() => {
        if (scale === 1) {
            setPosition({ x: 0, y: 0 });
        }
    }, [scale]);

    return (
        <div 
            ref={containerRef}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            className="position-relative w-100 h-100 overflow-hidden d-flex align-items-center justify-content-center bg-white"
            style={{ 
                minHeight: "360px",
                cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                userSelect: "none",
                ...style
            }}
        >
            <div
                style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transformOrigin: scale === 1 ? "center center" : "0 0",
                    transition: isDragging ? "none" : "transform 0.12s ease-out",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <img 
                    src={src} 
                    alt={alt} 
                    className={className}
                    draggable={false}
                    style={{ 
                        maxHeight: "320px", 
                        maxWidth: "100%", 
                        objectFit: "contain",
                        pointerEvents: "none"
                    }}
                />
            </div>

            {/* Yakınlaştırma / Uzaklaştırma Kontrol Paneli */}
            <div 
                className="position-absolute bottom-0 end-0 m-3 d-flex gap-2 p-1.5 rounded-3 bg-white shadow border"
                style={{ zIndex: 10, borderColor: "#e2e8f0" }}
                onMouseDown={e => e.stopPropagation()} // Panel üzerinden sürüklemeyi engelle
            >
                <button 
                    type="button"
                    className="btn btn-sm btn-light border-0 d-flex align-items-center justify-content-center p-2 rounded-2"
                    style={{ width: "32px", height: "32px", color: "#475569" }}
                    onClick={handleZoomIn}
                    title="Yakınlaştır"
                >
                    <IconZoomIn size={18} />
                </button>
                <button 
                    type="button"
                    className="btn btn-sm btn-light border-0 d-flex align-items-center justify-content-center p-2 rounded-2"
                    style={{ width: "32px", height: "32px", color: "#475569" }}
                    onClick={handleZoomOut}
                    disabled={scale === 1}
                    title="Uzaklaştır"
                >
                    <IconZoomOut size={18} />
                </button>
                <button 
                    type="button"
                    className="btn btn-sm btn-light border-0 d-flex align-items-center justify-content-center p-2 rounded-2"
                    style={{ width: "32px", height: "32px", color: "#475569" }}
                    onClick={handleReset}
                    disabled={scale === 1 && position.x === 0 && position.y === 0}
                    title="Sıfırla (1:1)"
                >
                    <IconRotateDot size={18} />
                </button>
                {scale > 1 && (
                    <div 
                        className="d-flex align-items-center justify-content-center text-secondary px-2 font-monospace"
                        style={{ fontSize: "11px", fontWeight: "bold" }}
                    >
                        %{Math.round(scale * 100)}
                    </div>
                )}
            </div>
            
            {/* Kullanım Yardım İpucu */}
            {scale === 1 && (
                <div 
                    className="position-absolute top-0 start-0 m-3 p-2 rounded bg-dark bg-opacity-75 text-white d-flex align-items-center gap-1.5"
                    style={{ fontSize: "11px", pointerEvents: "none", zIndex: 10, opacity: 0.85 }}
                >
                    <IconHandGrab size={13} />
                    <span>Fare tekerleğiyle yakınlaşın, basılı tutarak sürükleyin.</span>
                </div>
            )}
        </div>
    );
};
